// Google Analytics 4: what visitors do on buildbotsnow.com between landing and
// building a bot, so an ad's clicks can be followed past the click. The
// property's measurement id is below; without one, everything here does
// nothing.
//
// It rides on the Google tag lib/ads.ts already loads, rather than the
// Firebase Analytics SDK: one tag, one script, and only where that tag runs,
// on our own site (never the widget or a bot's share page).
//
// What is sent is cleaned first. Addresses lose their query string except the
// ad-click and campaign parameters attribution needs, because some of ours
// carry one-time codes (/account/confirm?oobCode=, /invite/<token>); ids in a
// path become placeholders. No event carries an email, a name or anything a
// customer typed.

// The stream of the Firebase web app "Build Bots Now"
// (1:134938846551:web:687ccd80d726d76d06ca12), so the data shows in the
// Firebase console's Analytics as well as in Google Analytics (property
// 553846393). Its enhanced measurement is off, so every page view is one of
// ours, cleaned. Also in RTDB /config/googleAnalytics.
export const GA_MEASUREMENT_ID = "G-C4QZTYHMCM";

type Gtag = (...args: unknown[]) => void;
type AnalyticsWindow = Window & { gtag?: Gtag };

const KEPT_PARAMS = /^(gclid|gbraid|wbraid|utm_[a-z]+)$/;
const SCROLL_MARKS = [25, 50, 75, 90];
const HOME_SECTIONS = ["how", "features", "pricing", "faq"];

let active: { win: AnalyticsWindow; id: string } | null = null;
// Per page view: which scroll marks and home sections were already reported.
let seen = new Set<string>();

// "/agents/abc123/settings" -> "/agents/:id/settings"; tokens never leave.
export function normalizePath(pathname: string): string {
  return pathname
    .replace(/^\/agents\/(?!new(\/|$))[^/]+/, "/agents/:id")
    .replace(/^\/invite\/[^/]+/, "/invite/:token");
}

export function cleanUrl(href: string): string {
  const url = new URL(href);
  const kept = new URLSearchParams();
  url.searchParams.forEach((value, key) => {
    if (KEPT_PARAMS.test(key)) kept.set(key, value);
  });
  const query = kept.toString();
  return `${url.origin}${normalizePath(url.pathname)}${query ? `?${query}` : ""}`;
}

// Every event carries the page it happened on: the tag would otherwise label
// it with the address the visit started on, however far the visitor went.
export function track(name: string, params: Record<string, unknown> = {}): boolean {
  if (!active) return false;
  active.win.gtag?.("event", name, { page_location: cleanUrl(active.win.location.href), ...params, send_to: active.id });
  return true;
}

export function trackPageView(): boolean {
  if (!active) return false;
  seen = new Set();
  const { location, document } = active.win;
  return track("page_view", {
    page_location: cleanUrl(location.href),
    page_path: normalizePath(location.pathname),
    page_title: document.title
  });
}

// How far down the page, and on the home page which sections came into view.
function onScroll(win: AnalyticsWindow) {
  const doc = win.document.documentElement;
  const scrollable = doc.scrollHeight - win.innerHeight;
  // A page that fits the window has no depth to report: the scroll event a
  // page change fires would otherwise count as reading all of it.
  const percent = scrollable > 0 ? (win.scrollY / scrollable) * 100 : 0;
  for (const mark of SCROLL_MARKS) {
    if (percent >= mark && !seen.has(`scroll:${mark}`)) {
      seen.add(`scroll:${mark}`);
      track("scroll_depth", { percent: mark, page_path: normalizePath(win.location.pathname) });
    }
  }
  if (win.location.pathname !== "/") return;
  for (const id of HOME_SECTIONS) {
    const top = win.document.getElementById(id)?.getBoundingClientRect().top;
    if (top !== undefined && top < win.innerHeight * 0.6 && !seen.has(`section:${id}`)) {
      seen.add(`section:${id}`);
      track("view_section", { section: id });
    }
  }
}

// A click on a link to start building or to sign in, from anywhere: which
// button, and in which part of the page. In-page links say where they went.
function onClick(win: AnalyticsWindow, event: Event) {
  const link = (event.target as Element | null)?.closest?.("a") as HTMLAnchorElement | null;
  if (!link) return;
  const href = link.getAttribute("href") || "";
  const section = link.closest("section[id], header, footer");
  const where = section?.id || section?.tagName.toLowerCase() || "page";
  // innerText skips the wording hidden at this screen size; a button with a
  // short and a long label for phone and desktop would otherwise read as both.
  const label = (link.innerText || link.textContent || "").replace(/\s+/g, " ").trim().slice(0, 40);
  if (href.startsWith("#")) {
    track("nav_click", { target: href.slice(1), location: where });
    return;
  }
  const path = new URL(href, win.location.href);
  if (path.origin !== win.location.origin) return;
  if (path.pathname === "/start" || path.pathname === "/signin") {
    track("cta_click", {
      cta: path.pathname === "/start" ? "start" : path.searchParams.get("mode") === "signup" ? "signup" : "signin",
      label,
      location: where,
      page_path: normalizePath(win.location.pathname)
    });
  }
}

// Turns it on, once: only with a property id, and only where the Google tag
// is on the page (lib/ads.ts decides that). Page views are sent by
// <PageViews />, so the tag's own first one, with the raw address, is off.
export function startAnalytics({
  win = window as AnalyticsWindow,
  measurementId = GA_MEASUREMENT_ID
}: { win?: AnalyticsWindow; measurementId?: string } = {}): boolean {
  if (active) return true;
  if (!measurementId || !win.gtag) return false;
  win.gtag("config", measurementId, { send_page_view: false, page_location: cleanUrl(win.location.href) });
  active = { win, id: measurementId };
  let queued = false;
  win.addEventListener(
    "scroll",
    () => {
      if (queued) return;
      queued = true;
      win.requestAnimationFrame(() => {
        queued = false;
        onScroll(win);
      });
    },
    { passive: true }
  );
  win.document.addEventListener("click", (event) => onClick(win, event), { capture: true });
  return true;
}

// For the tests.
export function stopAnalytics() {
  active = null;
  seen = new Set();
}
